import { FC, useState, createContext, useEffect, useCallback, ReactNode } from 'react';
import { graphql, useFragment } from 'react-relay';

import {
    trackEcommerceProductImpressions,
    trackEcommerceProductClick,
} from '../../utils/tracking/searchBrowse/ecommerceTracking';
import { getUserSessionRegions } from 'dibs-regional-info/exports/regionalInfoHelpers';

import { SbSharedThinBrowseItemTrackingProvider_items$key } from './__generated__/SbSharedThinBrowseItemTrackingProvider_items.graphql';
import { SbSharedThinBrowseItemTrackingProvider_itemSearch$key } from './__generated__/SbSharedThinBrowseItemTrackingProvider_itemSearch.graphql';
import { SbSharedThinBrowseItemTrackingProvider_viewer$key } from './__generated__/SbSharedThinBrowseItemTrackingProvider_viewer.graphql';

type ThinBrowseItemTrackingContextType = {
    fireThinBrowseItemsImpressionTracking: (index: number) => void;
    fireItemClickTracking: (index: number) => void;
};

export const SbSharedThinBrowseItemTrackingContext =
    createContext<ThinBrowseItemTrackingContextType>({
        fireThinBrowseItemsImpressionTracking: () => {},
        fireItemClickTracking: () => {},
    });

type Props = {
    children: ReactNode;
    thinBrowseListingType: string;
    itemSearch: SbSharedThinBrowseItemTrackingProvider_itemSearch$key;
    items: SbSharedThinBrowseItemTrackingProvider_items$key;
    viewer: SbSharedThinBrowseItemTrackingProvider_viewer$key;
};

export const SbSharedThinBrowseItemTrackingProvider: FC<Props> = ({
    children,
    itemSearch: itemSearchRef,
    items: itemsRef,
    thinBrowseListingType,
    viewer: viewerRef,
}) => {
    const itemSearch = useFragment(
        graphql`
            fragment SbSharedThinBrowseItemTrackingProvider_itemSearch on ItemSearchQueryConnection {
                searchTerm
            }
        `,
        itemSearchRef
    );
    const items = useFragment(
        graphql`
            fragment SbSharedThinBrowseItemTrackingProvider_items on Item @relay(plural: true) {
                serviceId
                title
                categoryPath
                browseUrl
                categoryCode
                contemporaryTrackingString
                seller {
                    serviceId
                }
                displayPriceTracking: displayPrice(
                    page: searchAndBrowse
                    isTrade: $isTrade
                    userCountryCode: $userCountryCode
                    priceBookName: $priceBookName
                ) {
                    convertedAmountList {
                        amount
                        currency
                    }
                    amountType
                    textType
                }
                isAuctionEnabled
                ...shippingCostHelpers_item
            }
        `,
        itemsRef
    );
    const viewer = useFragment(
        graphql`
            fragment SbSharedThinBrowseItemTrackingProvider_viewer on Viewer {
                regionalInfo(zipCode: $userZipCode, countryCode: $userCountryCode)
                    @include(if: $fetchRegionalInfo) {
                    regionsByZipCode {
                        displayName
                    }
                }
            }
        `,
        viewerRef
    );
    const [hasTrackedRecentlySoldItems, setHasTrackedRecentlySoldItems] = useState(false);
    const [attemptedItemImpression, setAttemptedItemImpression] = useState(false);

    const userRegions = useCallback(
        () => getUserSessionRegions() || viewer?.regionalInfo?.[0]?.regionsByZipCode || [],
        [viewer]
    );
    const regionalInfoAvailable = useCallback(
        () => !!(getUserSessionRegions() || viewer?.regionalInfo),
        [viewer]
    );

    const fireThinBrowseItemsImpressionTracking = useCallback(
        (index: number) => {
            if (!regionalInfoAvailable) {
                setAttemptedItemImpression(true);
            } else if (!hasTrackedRecentlySoldItems && index === 0) {
                const { searchTerm } = itemSearch || {};

                trackEcommerceProductImpressions({
                    items: items.map(item => ({
                        node: item,
                    })),
                    pageType: thinBrowseListingType,
                    startIndex: index,
                    searchTerm,
                    userRegions: userRegions(),
                });
                setHasTrackedRecentlySoldItems(true);
            }
        },
        [
            hasTrackedRecentlySoldItems,
            itemSearch,
            items,
            regionalInfoAvailable,
            thinBrowseListingType,
            userRegions,
        ]
    );

    const fireItemClickTracking = (index: number): void => {
        const { searchTerm } = itemSearch || {};
        const item = items[index];

        trackEcommerceProductClick({
            item,
            index,
            pageType: thinBrowseListingType,
            searchTerm,
            userRegions: userRegions(),
        });
    };

    useEffect(() => {
        if (regionalInfoAvailable() && attemptedItemImpression) {
            fireThinBrowseItemsImpressionTracking(0);
            setAttemptedItemImpression(false);
        }
    }, [attemptedItemImpression, fireThinBrowseItemsImpressionTracking, regionalInfoAvailable]);

    return (
        <SbSharedThinBrowseItemTrackingContext.Provider
            value={{
                fireThinBrowseItemsImpressionTracking,
                fireItemClickTracking,
            }}
        >
            {children}
        </SbSharedThinBrowseItemTrackingContext.Provider>
    );
};
